import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';

const PREF_KEY_HIDDEN = 'situation_hidden_widgets';
const PREF_KEY_ACCENTS = 'situation_widget_accents';
// 2026-06-09 통합 키 — 본인/큰카드/admin 위젯 한 순서로 통합.
const PREF_KEY_ORDER_UNIFIED = 'situation_widget_order';
// 레거시 키 (read 호환만) — 신규 키 비어 있으면 두 키 merge.
const PREF_KEY_ORDER_PERSONAL_LEGACY = 'situation_widget_order_personal';
const PREF_KEY_ORDER_ADMIN_LEGACY = 'situation_widget_order_admin';

// 주어진 codes 를 order 배열에 맞춰 정렬.
// - order 안에 있는 코드는 order 순서대로 (codes 안에 존재하는 것만).
// - order 에 없는 코드는 기본 순서(codes 원순서) 그대로 뒤에 붙음.
// → 새로 추가된 위젯이 자동으로 끝에 표시되고, 사용자가 드래그한 위젯은 저장된 순서 유지.
export function reorderByPreference(codes, order) {
  if (!Array.isArray(order) || order.length === 0) return codes;
  const codeSet = new Set(codes);
  const inOrder = order.filter((c) => codeSet.has(c));
  const orderSet = new Set(inOrder);
  const rest = codes.filter((c) => !orderSet.has(c));
  return [...inOrder, ...rest];
}

export function useSituationWidgets({ showToast }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [widgets, setWidgets] = useState([]);
  const [hiddenCodes, setHiddenCodes] = useState([]);
  // accentMap: { [widgetCode]: accentKey } — 사용자가 지정한 위젯별 색상 override
  const [accentMap, setAccentMap] = useState({});
  // 위젯 드래그 순서 — 2026-06-09 통합 단일 배열 (personal + bigCards + admin)
  const [orderUnified, setOrderUnified] = useState([]);
  // 위젯 코드 → 메뉴 URL 매핑 — 2026-06-16 (위젯 짧게 클릭 시 navigate)
  const [widgetMenuMap, setWidgetMenuMap] = useState({});
  // 누르면 갈 메뉴를 못 여는 위젯(손님 테넌트 직원 — 2026-10-24). 누르면 그 메뉴 안에 "이 메뉴는 담당자가 쓰는 화면이에요".
  const [noAccessCodes, setNoAccessCodes] = useState([]);
  // '내일 방문' 카드 칸 이름 — 회사들의 일일콜 분류(2026-10-25, 서버 visit_categories)
  const [visitCategories, setVisitCategories] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await apiClient.get('/api/my/situation-widgets');
    if (!res.ok || res.data?.success === false) {
      const message = res.data?.message || '상황판 위젯을 불러오지 못했습니다.';
      setError(message);
      setWidgets([]);
      setHiddenCodes([]);
      setAccentMap({});
      setOrderUnified([]);
      setLoading(false);
      return;
    }
    setWidgets(Array.isArray(res.data?.widgets) ? res.data.widgets : []);
    setHiddenCodes(Array.isArray(res.data?.hidden_widget_codes) ? res.data.hidden_widget_codes : []);
    setVisitCategories(Array.isArray(res.data?.visit_categories) ? res.data.visit_categories.filter((c) => typeof c === 'string' && c) : []);

    // 색상 preference 별도 로드 — 응답에 없어도 무방
    try {
      const accentRes = await apiClient.get(`/api/my/preferences/${PREF_KEY_ACCENTS}`);
      if (accentRes.ok && accentRes.data && typeof accentRes.data.value === 'object' && accentRes.data.value) {
        const raw = accentRes.data.value;
        const cleaned = {};
        for (const [code, color] of Object.entries(raw)) {
          if (typeof code === 'string' && typeof color === 'string' && code && color) {
            cleaned[code] = color;
          }
        }
        setAccentMap(cleaned);
      } else {
        setAccentMap({});
      }
    } catch (_e) {
      setAccentMap({});
    }

    // 위젯 → 메뉴 URL 매핑 fetch (위젯 짧게 클릭 navigate 용) — 실패해도 진행
    try {
      const mapRes = await apiClient.get('/api/my/widget-menu-mapping');
      if (mapRes.ok && mapRes.data && typeof mapRes.data.mapping === 'object' && mapRes.data.mapping) {
        const raw = mapRes.data.mapping;
        const cleaned = {};
        for (const [code, url] of Object.entries(raw)) {
          if (typeof code === 'string' && typeof url === 'string' && code && url) {
            cleaned[code] = url;
          }
        }
        setWidgetMenuMap(cleaned);
        const na = mapRes.data.no_access;
        setNoAccessCodes(Array.isArray(na) ? na.filter((c) => typeof c === 'string' && c) : []);
      } else {
        setWidgetMenuMap({});
        setNoAccessCodes([]);
      }
    } catch (_e) {
      setWidgetMenuMap({});
      setNoAccessCodes([]);
    }

    // 순서 preference 로드 — 신규 단일 키 우선, 없으면 레거시 두 키 merge (personal → admin).
    try {
      const unifiedRes = await apiClient.get(`/api/my/preferences/${PREF_KEY_ORDER_UNIFIED}`);
      const uVal = unifiedRes.ok ? unifiedRes.data?.value : null;
      const unifiedArr = Array.isArray(uVal) ? uVal.filter((c) => typeof c === 'string' && c) : [];
      if (unifiedArr.length > 0) {
        setOrderUnified(unifiedArr);
      } else {
        // 레거시 fallback — 두 키 merge (중복 제거, personal 앞)
        const [personalRes, adminRes] = await Promise.all([
          apiClient.get(`/api/my/preferences/${PREF_KEY_ORDER_PERSONAL_LEGACY}`),
          apiClient.get(`/api/my/preferences/${PREF_KEY_ORDER_ADMIN_LEGACY}`),
        ]);
        const pVal = personalRes.ok ? personalRes.data?.value : null;
        const aVal = adminRes.ok ? adminRes.data?.value : null;
        const pArr = Array.isArray(pVal) ? pVal.filter((c) => typeof c === 'string' && c) : [];
        const aArr = Array.isArray(aVal) ? aVal.filter((c) => typeof c === 'string' && c) : [];
        const merged = Array.from(new Set([...pArr, ...aArr]));
        setOrderUnified(merged);
      }
    } catch (_e) {
      setOrderUnified([]);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const hiddenSet = useMemo(() => new Set(hiddenCodes), [hiddenCodes]);
  const visibleWidgets = useMemo(
    () => widgets.filter((widget) => !hiddenSet.has(widget.code)),
    [widgets, hiddenSet]
  );
  const visibleCodes = useMemo(() => visibleWidgets.map((widget) => widget.code), [visibleWidgets]);

  const saveHiddenCodes = useCallback(async (nextCodes) => {
    const normalized = [...new Set((nextCodes || []).map(String).filter(Boolean))];
    setHiddenCodes(normalized);
    const res = await apiClient.put(`/api/my/preferences/${PREF_KEY_HIDDEN}`, { value: normalized });
    if (!res.ok) {
      showToast?.(res.data?.message || '위젯 숨김 설정 저장 실패', 'error');
      load();
      return false;
    }
    return true;
  }, [load, showToast]);

  const toggleHidden = useCallback((widgetCode) => {
    const code = String(widgetCode || '');
    if (!code) return;
    const next = hiddenSet.has(code)
      ? hiddenCodes.filter((item) => item !== code)
      : [...hiddenCodes, code];
    saveHiddenCodes(next);
  }, [hiddenCodes, hiddenSet, saveHiddenCodes]);

  // 위젯별 accent 색상 변경 — optimistic + PUT 저장
  const setAccentForCode = useCallback(async (widgetCode, accentKey) => {
    const code = String(widgetCode || '');
    if (!code) return false;
    const next = { ...accentMap };
    if (accentKey === null || accentKey === undefined || accentKey === '') {
      delete next[code];
    } else {
      next[code] = String(accentKey);
    }
    setAccentMap(next);
    const res = await apiClient.put(`/api/my/preferences/${PREF_KEY_ACCENTS}`, { value: next });
    if (!res.ok) {
      showToast?.(res.data?.message || '위젯 색상 저장 실패', 'error');
      // 실패 시 이전 상태로 되돌리기
      setAccentMap(accentMap);
      return false;
    }
    return true;
  }, [accentMap, showToast]);

  // 위젯 드래그 순서 저장 — 2026-06-09 통합 단일 함수.
  // nextOrder: 사용자가 드래그 후 결정된 새 순서 (personal + bigCards + admin 모두 포함).
  // 실패 시 이전 상태로 되돌림.
  const setWidgetOrder = useCallback(async (nextOrder) => {
    const normalized = Array.from(new Set((nextOrder || []).map(String).filter(Boolean)));
    const prev = orderUnified;
    setOrderUnified(normalized);
    const res = await apiClient.put(`/api/my/preferences/${PREF_KEY_ORDER_UNIFIED}`, { value: normalized });
    if (!res.ok) {
      showToast?.(res.data?.message || '위젯 순서 저장 실패', 'error');
      setOrderUnified(prev);
      return false;
    }
    return true;
  }, [orderUnified, showToast]);

  return {
    loading,
    error,
    widgets,
    visibleWidgets,
    visibleCodes,
    hiddenCodes,
    hiddenSet,
    accentMap,
    orderUnified,
    widgetMenuMap,
    noAccessCodes,
    visitCategories,
    reloadWidgets: load,
    toggleHidden,
    setAccentForCode,
    setWidgetOrder,
  };
}
